import React, {FC} from "react";
import {Alert, Button, Form, Radio, Space, Typography, Upload} from "antd";
import {UploadOutlined} from "@ant-design/icons";
import {buildPaymentMethodRules} from "./formRules";
import {paymentMethodLabel} from "./publicRegistrationModel";
import {useRegistrationCopy} from "./registrationCopy";
import type {PaymentMethodValue, PublicParticipationFeeOption} from "./types";

export const ParticipationFeeField: FC<{fees: PublicParticipationFeeOption[]}> = ({fees}) => {
    const {labels, participationFeeLabel} = useRegistrationCopy();

    if (fees.length === 0) {
        return <Alert type="warning" showIcon message={labels.participationFeeMissing}/>;
    }

    return (
        <Form.Item name="selectedParticipationFeePriceId" label={labels.participationFee} rules={[{required: true, message: labels.required}]}>
            <Radio.Group
                options={fees.map(fee => ({
                    value: fee.feePriceId,
                    label: participationFeeLabel(fee)
                }))}
            />
        </Form.Item>
    );
};

export const ParticipantTypeProofField: FC<{
    uploadedFileId?: number;
    uploadedFileName?: string;
    uploading: boolean;
    submitting: boolean;
    onUpload: (file: File) => void;
}> = ({uploadedFileId, uploadedFileName, uploading, submitting, onUpload}) => {
    const {labels} = useRegistrationCopy();

    return (
        <Form.Item
            label={labels.participantTypeProof}
            required
            validateStatus={uploadedFileId ? "success" : undefined}
            help={uploadedFileId ? labels.participantTypeProofUploaded : labels.participantTypeProofHelp}
        >
            <Space wrap>
                <Upload
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                    disabled={uploading || submitting}
                    maxCount={1}
                    showUploadList={false}
                    beforeUpload={(file) => {
                        onUpload(file);
                        return false;
                    }}
                >
                    <Button
                        icon={<UploadOutlined/>}
                        loading={uploading}
                        disabled={submitting}
                    >
                        {uploading ? labels.participantTypeProofUploading : labels.participantTypeProofUpload}
                    </Button>
                </Upload>
                {uploadedFileName ? (
                    <Typography.Text type="secondary">{uploadedFileName}</Typography.Text>
                ) : null}
            </Space>
        </Form.Item>
    );
};

// Payment method choice for one offer category; warns when the event configures none.
export const PaymentMethodField: FC<{
    name: "participationPaymentMethod" | "accommodationPaymentMethod";
    methods: PaymentMethodValue[];
}> = ({name, methods}) => {
    const {labels} = useRegistrationCopy();

    if (methods.length === 0) {
        return <Alert type="warning" showIcon message={labels.missingPaymentMethods}/>;
    }

    const isParticipation = name === "participationPaymentMethod";
    return (
        <Form.Item
            name={name}
            label={isParticipation ? labels.participationPaymentMethod : labels.accommodationPaymentMethod}
            rules={isParticipation
                ? buildPaymentMethodRules(methods, labels)
                : [{required: true, message: labels.required}]}
        >
            <Radio.Group
                options={methods.map(method => ({
                    value: method,
                    label: paymentMethodLabel(method, labels)
                }))}
            />
        </Form.Item>
    );
};
